import React from "react";
import { useState, useEffect, useRef, useCallback, useMemo  } from "react";
import useSWR from "swr";
import { list } from "../api/api";
import { ContactTable } from "../components/ContactTable";
import { Upload } from "../components/Upload";
import ExpandableSection from "@cloudscape-design/components/expandable-section";
import { useHistory } from 'react-router-dom';
import { AlertBanner } from "../components/AlertBanner";
import { DashboardMetrics } from "../components/DashboardMetrics";
import { useDangerAlert } from "../hooks/useAlert";
import { presign } from "../api/api";
import { useUploadProgress } from '../hooks/UploadProgressContext';
import AudioProcessingService from '../util/audioProcessingService';
import TutorialGuide from '../components/TutorialGuide';

import {
  Button,
  ContentLayout,
  Link,
  Header,
  BreadcrumbGroup,
  Grid,
  FileUpload,
  FormField,
  Form,
  SpaceBetween,
  Spinner,
  ColumnLayout,
  Container,
  ProgressBar,
  StatusIndicator,  
  Box
} from '@cloudscape-design/components';

import { useTranslation } from 'react-i18next';
import "../locales/i18n";
import { useUserRole } from "../api/role";
import { useInteractionType } from "../hooks/InteractionTypeContext";

const config = window.pcaSettings;


function Home({ setAlert }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageKeys, setPageKeys] = useState([]);
  const [showAlertBanner, setShowAlertBanner] = useState(true);
  const { isFirstLogin, markFirstLoginComplete, isLoading } = useUserRole();
  const {
    calculateOverallProgress,
    showProcessingProgress,
    uploadItems,
    setIsModalVisible,
    queueInfo,
  } = useUploadProgress();
  const { interactionType, isTextMode } = useInteractionType();

  const PAGE_SIZE = 20;
  const { hasPermission } = useUserRole();

  const tutorialSteps = useMemo(() => [
    {
      title: "¡Bienvenido a EVA! 🎉",
      content: (
        <div>
          <p>Esta guía te mostrará cómo utilizar Evoltis Voice Analytics paso a paso.</p>
          <p>Aprenderás a cargar audios, monitorear su procesamiento y analizar los resultados.</p>
        </div>
      ),
      selector: null,
      position: "center"
    },
    {
      title: "1. Personalizar columnas 🔧",
      content: (
        <div>
          <p>Personaliza qué información ver en la tabla de llamadas.</p>
          <p>Haz clic en este ícono para <strong>seleccionar las columnas</strong> que deseas mostrar.</p>
        </div>
      ),
      selector: "#table-preferences-trigger",
      position: "left"
    },
    {
      title: "2. Búsqueda avanzada 🔍",
      content: (
        <div>
          <p>Utiliza la <strong>búsqueda avanzada</strong> para encontrar llamadas específicas.</p>
          <p>Puedes filtrar por fecha, agente, GUID, duración y más criterios.</p>
        </div>
      ),
      selector: "[data-testid='property-filter']",
      position: "top"
    },
    {
      title: "3. Búsqueda global 🔍",
      content: (
        <div>
          <p>Utiliza el <strong>botón de búsqueda global</strong> en el header para acceder a funciones de búsqueda avanzada.</p>
          <p>Encuentra llamadas específicas con filtros detallados.</p>
        </div>
      ),
      selector: "[data-testid='header-search-button']",
      position: "bottom",
    },
    {
      title: "4. Dashboard principal 📊",
      content: (
        <div>
          <p>Accede al <strong>dashboard principal</strong> con visualizaciones completas.</p>
          <p>Análisis en tiempo real y métricas avanzadas de tus datos.</p>
        </div>
      ),
      selector: "[data-testid='header-insights-button']",
      position: "bottom",
    },
    {
      title: "5. Centro de notificaciones 🔔",
      content: (
        <div>
          <p>Mantente informado con el <strong>centro de notificaciones</strong>.</p>
          <p>Recibe alertas sobre procesamiento de audios y actualizaciones del sistema.</p>
        </div>
      ),
      selector: "[data-testid='header-notifications']",
      position: "bottom"
    },
    {
      title: "6. Menú de usuario 👤",
      content: (
        <div>
          <p>Gestiona tu perfil y configuraciones desde el <strong>menú de usuario</strong>.</p>
          <p>Revisa tu rol, versión del sistema y opciones de cuenta.</p>
        </div>
      ),
      selector: "[data-testid='header-user-menu']",
      position: "bottom"
    },
    {
      title: "¡Tutorial completado! ✅",
      content: (
        <div>
          <p><strong>¡Perfecto!</strong> Ya conoces todas las funciones principales de EVA.</p>
          <p>Puedes volver a ver esta guía en cualquier momento usando el botón "?".</p>
          <p>¡Comienza a analizar tus llamadas! 🚀</p>
        </div>
      ),
      selector: null,
      position: "center"
    }
  ], []);

  const fetcher = async (page) => {
    const opts = {
      count: PAGE_SIZE,
      interactionType: interactionType
    };

    if (page > 1) {
      const previousPageKey = pageKeys[page - 2];
      if (previousPageKey) {
        opts.startKey = previousPageKey.StartKey;
        opts.timestampFrom = previousPageKey.timestampFrom;
      }
    }

    const response = await list(opts);

    if (response.StartKey && response.timestampFrom) {
      setPageKeys(prev => {
        const newKeys = [...prev];
        newKeys[page - 1] = {
          StartKey: response.StartKey,
          timestampFrom: response.timestampFrom
        };
        return newKeys;
      });
    }

    return response;
  };

  const [totalPages, setTotalPages] = useState(1);

  // Reset pagination when interaction type changes
  useEffect(() => {
    setCurrentPage(1);
    setPageKeys([]);
    setTotalPages(1);
  }, [interactionType]);

  const { data, error } = useSWR([currentPage, interactionType], () => fetcher(currentPage));

  const checkActiveFiles = useCallback(async () => {
    try {
      const statusData = await AudioProcessingService.getSystemStatus();
      console.log("Estado del sistema:", statusData);
      
      const processing = Array.isArray(statusData.processing) 
        ? statusData.processing 
        : (statusData.processing ? [statusData.processing] : []);
      
      const queueSize = statusData.queueSize || 0;      
      if (processing.length === 0 && queueSize === 0) {
        return;
      }
      
      const activeFiles = processing.map(file => {
        const fileName = file.fileName || (file.fileId ? file.fileId.split('/').pop() : 'unknown');
        return {
          label: fileName,
          fileId: file.fileId,
          file: { name: fileName }
        };
      });
      
      const newProgress = {};
      activeFiles.forEach(item => {
        const file = processing.find(f => f.fileId === item.fileId);
        if (file) {
          newProgress[item.label] = {
            status: AudioProcessingService.mapStatusToUI(file.status || 'processing'),
            progress: AudioProcessingService.calculateProgress(file.status || 'processing'),
            error: file.error || ''
          };
        }
      });
      
      console.log(`Mostrando estado: ${activeFiles.length} archivos en proceso, ${queueSize} en cola`);
      showProcessingProgress(activeFiles, newProgress, queueSize);
      
    } catch (error) {
      console.error('Error verificando archivos activos:', error);
    }
  }, [showProcessingProgress]);

  const handleTutorialComplete = useCallback(() => {
    if (isFirstLogin) {
      markFirstLoginComplete();
    }
  }, [isFirstLogin, markFirstLoginComplete]);

useEffect(() => {
  if (data) {
    if (data.StartKey && currentPage >= totalPages) {
      setTotalPages(prev => prev + 1);
    }    
  }
}, [data, currentPage, totalPages]);

useEffect(() => {
    if (isFirstLogin && !isLoading) {
      localStorage.removeItem("eva_tutorial_seen");
    }
  }, [isFirstLogin, isLoading]);


  const handlePageChange = ({ detail }) => {
    const newPage = detail.currentPageIndex;
    setCurrentPage(newPage);
  };

  const { t } = useTranslation();

  const details = data ? data.Records : [];
  useDangerAlert(error, setAlert);

  useEffect(() => {
    checkActiveFiles();
  }, [checkActiveFiles]);

  return (
    <>
      <TutorialGuide 
        steps={tutorialSteps}
        storageKey="eva_tutorial_seen"
        autoStart={!isLoading}
        forceShow={isFirstLogin && !isLoading}
        onComplete={handleTutorialComplete}
        triggerButton={true}
      />
      <ContentLayout
        header={
            <Header
              variant="h1"
              description={isTextMode ? t('home.descriptionText') : t('home.description')}
              info={<Link variant="info" ariaLabel="Info goes here.">Info</Link>}>
              {isTextMode ? t('conversationList') : t('callList')}
            </Header>
        }>
        <Container>
          <Grid
            gridDefinition={[
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} },
              {colspan: { default:12} }
            ]}
          >
            {hasPermission("view_dashboard_metrics") && (
              <div id="dashboard-metrics-section">
                <DashboardMetrics data={data} />
              </div>
            )}
            {showAlertBanner && <AlertBanner />}
            {hasPermission("manage_audios") && (
              <div id="upload-section">
                <ExpandableSection headerText={t("home.uploadCall")}>
                  <Upload/>
                </ExpandableSection>
              </div>
            )}
            {(uploadItems.length > 0 || (queueInfo && queueInfo.queueSize > 0)) && (
              <div id="audio-status-section">
                <Container header={<Header variant="h2">{t('upload.progress', 'Progreso de archivos')}</Header>}>
                  <Box padding="m">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <div style={{ flexGrow: 1 }}>
                        <ProgressBar
                          value={calculateOverallProgress()}
                          label={`${calculateOverallProgress()}%`}
                          description={
                            <div>
                              {`${uploadItems.length} ${t('upload.files', 'archivos procesando')}`}
                              {queueInfo && (
                                <div>Archivos en cola: {queueInfo.queueSize}</div>
                              )}
                            </div>
                          }
                        />
                      </div>
                      <Button 
                        onClick={() => setIsModalVisible(true)}
                        iconName="external"
                        variant="link"
                        formAction="none"
                      >
                        {t('upload.viewDetails', 'Ver detalles')}
                      </Button>
                    </div>
                  </Box>
                </Container>
              </div>
            )}
            
            <ContactTable
              data={details}
              loading={!data && !error}
              empty={<Empty />}
              pagination={{
                currentPageIndex: currentPage,
                pagesCount: totalPages,
                onPageChange: handlePageChange
              }}
              onShowAlertBannerChange={setShowAlertBanner}
            />
            {/* <Button
            variant="normal"
            onClick={() => history.push('/analytics')}
          >
            {t("analytics.header")}
          </Button> */}
          </Grid>
        </Container>
        
      </ContentLayout>
      </>
  );
}

const Empty = () => (
  <div>
    <h2>No results</h2>
  </div>
);

export default Home;